<template>
  <div v-if="isLoading" id="loading-visualization"><div class="lds-ring"><div /><div /><div /><div /></div></div>
  <div v-else-if="!isLoading && !isEmpty" class="card-container scrollbar">
    <PublicDatasetCard
      v-for="d in publicDatasets"
      :key="d.id"
      :annotationSets="d.annotation_sets"
      :labels="d.labels"
      :dataset="d"
      class="list-card"
      @click="handleDatasetClicked(d)"
    />
  </div>
</template>

<script setup>
import {
  ref, computed, onMounted, onUnmounted, nextTick,
} from 'vue';
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import { useToast } from 'vue-toast-notification';
import PublicDatasetCard from "@/components/PublicProjects/PublicDatasetCard.vue";
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';

const dataConnect = new DatastoreConnect();

const $toast = useToast();
const route = useRoute();
const router = useRouter();
const store = useStore();

const isLoading = ref(true);
const publicDatasets = ref([]);
const isEmpty = computed(() => publicDatasets.value && publicDatasets.value.length === 0);
const controllers = ref([]);
const currentProject = computed(() => store.state.projects.currentProject);

onMounted(async () => {
  const projectId = currentProject.value?.id;
  if (projectId) {
    await getDatasets(projectId);

  const promises = publicDatasets.value.map((d) => getAnnotationSetsForDataset(d).then((refreshedAnnotationSets) => {
    setDatasetAnnotationSets(d, refreshedAnnotationSets);
  }));
  await Promise.all(promises);
  }
});

onUnmounted(() => {
  abortAllSignals();
});

async function getDatasets(project_id) {
  if (!project_id) return;

  isLoading.value = true;
  publicDatasets.value = await dataConnect.getDatasetList({
    project_id,
    get_num_images: true,
    get_annotation_sets: true,
    get_labels: true,
    get_num_labels: true,
    get_groups: true,
  })
    .then(async (data) => {
      if (!data.result) {
        throw data.error.message;
      }
      return data.result;
    })
    .catch((error) => {
      $toast.open({
        queue: true,
        dismissible: true,
        message: `Failed to retrieve experiment: ${error}`,
        type: "error",
        position: "top",
      });
    });

  isLoading.value = false;
}

async function getAnnotationSetsForDataset(dataset) {
  const controller = new AbortController();
  controllers.value.push(controller);

  const resp = await dataConnect.getAnnotationSets({
    dataset_id: dataset.id,
    get_num_annotations: true,
    get_num_images: true,
    get_labels: true,
  }, controller.signal)
    .catch((error) => {
      console.log(error);
    });
  if (!resp || resp.error || !resp.result) {
    return [];
  }
  return resp.result;
}

function setDatasetAnnotationSets(dataset, sets) {
  const datasetToUpdate = publicDatasets.value.find((d) => d.id === dataset.id);
  if (datasetToUpdate) {
    datasetToUpdate.annotation_sets = sets;
  }
}

function abortAllSignals() {
  controllers.value.forEach((controller) => {
    controller.abort();
  });
}

async function handleDatasetClicked(dataset) {
  await store.commit('datasets/setCurrentDataset', dataset);
  await nextTick();
  router.push({
    name: 'public.datasets.gallery.main',
    query: {
      dataset: dataset.id,
    },
  });
}

</script>

<style lang="scss" scoped>
.card-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(370px, 100%), 1fr));
  grid-auto-rows: 470px;
  width: 100%;
  gap: 16px;
  padding: 16px;
  overflow-y: auto;
  flex: 1 1 auto;
}

.list-card {
  height: 100%;
  width: 100%;
  cursor: pointer;
}
</style>
